import { Divider, Flex, IconButton, Text, Tooltip } from '@chakra-ui/react';
import { ReactComponent as ChevronIcon } from 'assets/icons/chevron.svg';
import { ReactComponent as DeleteIcon } from 'assets/icons/delete.svg';
import { ReactComponent as DuplicateIcon } from 'assets/icons/duplicate.svg';
import { ReactComponent as PlusIcon } from 'assets/icons/plus.svg';
import { PAGE_DELETED } from 'constants/mixpanel';
import { Mixpanel } from 'libs/third-party/Mixpanel/mixpanel';
import { RootState, addNewPage, duplicatePage, setActivePage } from 'modules/Editor/store';
import { selectIsViewOnly } from 'modules/Editor/store/editorSettingsSelector';
import { useHistory } from 'modules/Editor/store/history/historyManager';
import { useAppDispatch, useAppSelector, useAppStore } from 'modules/Editor/store/hooks';
import {
  selectHasReachedPageLimit,
  selectPage,
  selectPageOrder,
  selectWidgets,
} from 'modules/Editor/store/infographSelector';
import { removePage } from 'modules/Editor/store/infographSlice';
import { PageDirection } from 'modules/Editor/store/pageControlSlice.types';
import { selectActivePage } from 'modules/Editor/store/pageSelector';
import { PageNumberInput } from 'modules/common/components/Input/PageNumberInput';
import { ToastContent, useToast } from 'modules/common/components/Toast';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { createCopyPage } from '../Clipboard';
import { ToggleView } from './ToggleView';
import { PageManagerControlProps } from './config';

export const PageManagerControl = ({
  rightOffset,
  pageManagerToggleButtonRef,
  fromComponent,
}: PageManagerControlProps) => {
  const { undo } = useHistory();

  return (
    <Flex
      bg='white'
      borderTopLeftRadius={4}
      borderBottomLeftRadius={4}
      w='36px'
      position='absolute'
      right={rightOffset}
      top='25%'
      border='1px solid var(--vg-colors-gray-200)'
      zIndex='var(--vg-zIndices-pageManager)'
      p='2px'
    >
      <Flex w='100%' position='relative' gap={2} direction='column' align='center'>
        <ToggleView buttonRef={pageManagerToggleButtonRef} left='-36px' />
        <PageSwitcher />
        <Divider width={6} bg='gray.100' />
        <DuplicatePage undo={undo} />
        <AddPage undo={undo} />
        <DeletePage undo={undo} />
      </Flex>
    </Flex>
  );
};

const DeletePage = ({ undo }: { undo?: () => void }) => {
  const dispatch = useAppDispatch();

  const pageOrder = useAppSelector(selectPageOrder);
  const activePageId = useAppSelector(selectActivePage);
  const { t } = useTranslation(['editor_toolbar', 'editor_page_manager']);

  const toast = useToast();

  const isViewOnly = useAppSelector(selectIsViewOnly);

  const onDeletePage = () => {
    const isLast = activePageId === pageOrder[pageOrder.length - 1];
    const direction = isLast ? PageDirection.previous : PageDirection.next;

    dispatch(setActivePage({ direction }));
    dispatch(removePage({ pageId: activePageId }));

    const toastId = `delete-page-toast-${Date.now()}`;

    const handleClick = () => {
      if (undo) undo();
      toast.close(toastId);
    };

    toast({
      id: toastId,
      title: 'Page deleted',
      render: ({ title }) => (
        <ToastContent title={title} buttonTitle={t('editor_toolbar:undoBtn.text')} handleClick={handleClick} />
      ),
    });

    Mixpanel.track(PAGE_DELETED.eventName);
  };

  const label = t('editor_page_manager:delete-page');
  const hasOnePage = pageOrder?.length === 1;

  return (
    <Tooltip hasArrow placement='left' gutter={10} label={label} bg='black'>
      <IconButton
        isDisabled={isViewOnly || hasOnePage}
        size='sm'
        onClick={onDeletePage}
        variant='ghost'
        aria-label={label}
        icon={<DeleteIcon color='font.500' />}
      />
    </Tooltip>
  );
};

const AddPage = ({ undo }: { undo?: () => void }) => {
  const dispatch = useAppDispatch();

  const activePageId = useAppSelector(selectActivePage);
  const hasReachedPageLimit = useAppSelector(selectHasReachedPageLimit);
  const { t } = useTranslation(['editor_toolbar', 'editor_page_manager']);

  const toast = useToast();

  const isViewOnly = useAppSelector(selectIsViewOnly);

  const onAddPage = () => {
    if (hasReachedPageLimit) return;

    dispatch(addNewPage({ insertAfterId: activePageId }));

    const toastId = `add-page-toast-${Date.now()}`;

    const handleClick = () => {
      if (undo) undo();
      toast.close(toastId);
    };

    toast({
      id: toastId,
      title: 'Page added',
      render: ({ title }) => (
        <ToastContent title={title} buttonTitle={t('editor_toolbar:undoBtn.text')} handleClick={handleClick} />
      ),
    });

    Mixpanel.track('Page Added', {
      from: 'Multipage Menu',
      type: 'Blank',
    });
  };
  const label = t('editor_page_manager:add-page');

  return (
    <Tooltip hasArrow placement='left' gutter={10} label={label} bg='black'>
      <IconButton
        size='sm'
        onClick={onAddPage}
        isDisabled={isViewOnly || hasReachedPageLimit}
        variant='ghost'
        aria-label={label}
        icon={<PlusIcon />}
      />
    </Tooltip>
  );
};

const DuplicatePage = ({ undo }: { undo?: () => void }) => {
  const dispatch = useAppDispatch();
  const appStore = useAppStore();
  const hasReachedPageLimit = useAppSelector(selectHasReachedPageLimit);
  const { t } = useTranslation(['editor_toolbar', 'editor_page_manager']);

  const toast = useToast();

  const isViewOnly = useAppSelector(selectIsViewOnly);

  const onDuplicatePage = () => {
    if (hasReachedPageLimit) return;

    // we only need to access these values when onDuplicatePage is called (no useAppSelector subscription needed)
    const store = appStore.getState() as RootState;
    const activePageId = selectActivePage(store);
    const activePage = selectPage(activePageId)(store);
    const widgets = selectWidgets(store);

    const pageClipboards = createCopyPage(activePage, widgets);
    dispatch(duplicatePage({ insertAfterId: activePageId, pageClipboards }));

    const toastId = `duplicate-page-toast-${Date.now()}`;

    const handleClick = () => {
      if (undo) undo();
      toast.close(toastId);
    };

    toast({
      id: toastId,
      title: 'Page duplicated',
      render: ({ title }) => (
        <ToastContent title={title} buttonTitle={t('editor_toolbar:undoBtn.text')} handleClick={handleClick} />
      ),
    });

    Mixpanel.track('Page Duplicated', {
      from: 'Multipage Menu',
      type: 'Duplicate',
    });
  };
  const label = t('editor_page_manager:duplicate-page');

  return (
    <Tooltip hasArrow placement='left' gutter={10} label={label} bg='black'>
      <IconButton
        size='sm'
        variant='ghost'
        onClick={onDuplicatePage}
        aria-label={label}
        icon={<DuplicateIcon />}
        isDisabled={isViewOnly || hasReachedPageLimit}
      />
    </Tooltip>
  );
};

const PageSwitcher = () => {
  const { t } = useTranslation('editor_page_manager');
  const dispatch = useAppDispatch();

  const pageOrder = useAppSelector(selectPageOrder);
  const activePageId = useAppSelector(selectActivePage);
  const currentPageIdx = pageOrder.findIndex((pageId) => pageId === activePageId);
  const displayIdx = currentPageIdx + 1;
  const totalPageCount = pageOrder?.length || 0;

  const isFirstPage = currentPageIdx === 0;
  const isLastPage = currentPageIdx === pageOrder?.length - 1;

  const onNextPage = () => dispatch(setActivePage({ direction: PageDirection.next }));
  const onPreviousPage = () => dispatch(setActivePage({ direction: PageDirection.previous }));

  const previousPageLabel = t('previous-page-label');
  const nextPageLabel = t('next-page-label');

  const handleManualPageSwitch = useCallback(
    (pageNumber: number) => dispatch(setActivePage({ direction: PageDirection.manual, pageNumber })),
    [dispatch],
  );

  return (
    <Flex direction='column' align='center'>
      <Tooltip hasArrow placement='left' gutter={10} label={previousPageLabel} bg='black'>
        <IconButton
          size='sm'
          isDisabled={isFirstPage}
          onClick={onPreviousPage}
          variant='ghost'
          aria-label={previousPageLabel}
          icon={<ChevronIcon style={{ transform: 'rotate(180deg)' }} />}
        />
      </Tooltip>

      <PageNumberInput
        currentPageNumber={currentPageIdx + 1}
        totalPageCount={totalPageCount}
        handlePageSwitch={handleManualPageSwitch}
        numberInputProps={{
          mt: 1,
          mb: 1,
          size: 'sm',
          color: 'var(--vg-colors-font-300)',
          borderColor: 'gray.500',
        }}
      />
      <Text fontWeight='500' fontSize='xs' data-testid={`active-page-idx-${displayIdx}`}>
        {t('page-number-input', { totalCount: totalPageCount })}
      </Text>
      <Tooltip hasArrow placement='left' gutter={10} label={nextPageLabel} bg='black'>
        <IconButton
          size='sm'
          onClick={onNextPage}
          isDisabled={isLastPage}
          variant='ghost'
          aria-label={nextPageLabel}
          icon={<ChevronIcon />}
        />
      </Tooltip>
    </Flex>
  );
};
